import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { merge, map, Observable } from 'rxjs';
import { revisione53DatiFormSelectors } from '../../../store/selectors/dati-form.selectors';
import { SchedaCoopAgricoleFormValidators } from './validators/scheda-coop-agricole.form-validators';
import { manageSchedaDisabled, manageScObbl } from '../common/helper/scheda-settore.helper';
import { SchedaCoopAgricoleFormListeners } from './listeners/scheda-coop-agricole.form-listeners';
import { formPaths } from '@shared/constants/form-paths.const';
import { BaseRevisioneFormService } from '../../../abstracts/base-revisione-form.service';
import { DatiIdentificativiFormService } from '../../dati-identificativi/dati-identificativi-form.service';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { setInitialTipoSezEnte } from '../../../helpers/form-store.helpers';
import { withInitialValueDebounced } from '@shared/operators/with-initial-value-debounced.operator';
import { getSchedaCoopAgricoleControl } from './validators/helpers/scheda-coop-agricole-form-validators.helper';
import { toNum } from '@shared/helpers/math.helper';

@Injectable()
export class SchedaCoopAgricoleFormService extends BaseRevisioneFormService {
    isDisabled$: Observable<boolean>;

    constructor(protected datiIdentificativiService: DatiIdentificativiFormService) {
        super(undefined, undefined, datiIdentificativiService);
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_TipoSezEnte`,
            `${formPaths.datiIdentificativi}.my_ckSezAlboCorretta`,

            // Scheda altre coop
            `${formPaths.schedaAltreCoop}.my_CkAltreCoopAllegaSchedaAgricole`,
            `${formPaths.schedaAltreCoop}.my_ScAltreCoopObbl`,
            `${formPaths.schedaAltreCoop}.my_CkAltreCoopAllegaSchedaAgricoleConferimento`,
            `${formPaths.schedaAltreCoop}.my_CkAltreCoopAllegaSchedaAgricoleConsorzi`,
        ]);
    }

    get disponibilitaTerreni$(): Observable<{ sommaTerreni: number; sommaTerreniConduzione: number }> {
        const proprieta = getSchedaCoopAgricoleControl(this._form, 'my_TerreniProprieta');
        const fitto = getSchedaCoopAgricoleControl(this._form, 'my_TerreniInFitto');
        const altroTitolo = getSchedaCoopAgricoleControl(this._form, 'my_TerreniAdAltroTitolo');
        const condUnica = getSchedaCoopAgricoleControl(this._form, 'my_TerreniConduzioneUnica');
        const condDivisa = getSchedaCoopAgricoleControl(this._form, 'my_TerreniConduzioneDivisa');
        return merge(
            proprieta.valueChanges.pipe(withInitialValueDebounced(proprieta)),
            fitto.valueChanges.pipe(withInitialValueDebounced(fitto)),
            altroTitolo.valueChanges.pipe(withInitialValueDebounced(altroTitolo)),
            condUnica.valueChanges.pipe(withInitialValueDebounced(condUnica)),
            condDivisa.valueChanges.pipe(withInitialValueDebounced(condDivisa)),
        ).pipe(
            map(() => ({
                sommaTerreni: toNum(proprieta.value) + toNum(fitto.value) + toNum(altroTitolo.value),
                sommaTerreniConduzione: toNum(condUnica.value) + toNum(condDivisa.value),
            }))
        );
    }

    override initListener(): void {
        new SchedaCoopAgricoleFormListeners(this._form, this.store).setListeners();
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_SezioneI__Rilevazione: this.fb.group({
                    my_DatiIdentificativi: this.fb.group({
                        my_TipoSezEnte: '',
                        my_ckSezAlboCorretta: '',
                    }),
                    my_SchedaCoopAltreCoop: this.fb.group({
                        my_CkAltreCoopAllegaSchedaAgricole: '',
                        my_ScAltreCoopObbl: '',
                        my_CkAltreCoopAllegaSchedaAgricoleConferimento: '',
                        my_CkAltreCoopAllegaSchedaAgricoleConsorzi: '',
                    }),
                    my_SchedaCoopAgricole: this.fb.group({
                        my_ScAgricoleObbl: '',
                        my_GroupAgricole: '',
                        my_CkFruisceProvvidenze: '',
                        my_ColturePraticate: '',
                        my_TerreniProprieta: '',
                        my_TerreniInFitto: '',
                        my_TerreniAdAltroTitolo: '',
                        my_TerreniConduzioneUnica: '',
                        my_TerreniConduzioneDivisa: '',
                        my_ProvvidenzePerCalamita: '',
                        my_CkFornisceServiziSoci: '',
                        my_ElencoServiziSoci: '',
                        my_CkFornisceServiziNonSoci: '',
                        my_ElencoServiziNonSoci: '',
                        my_CkStabilimentoInProprieta: '',
                        my_StabilimentoAdAltroTitolo: '',
                        my_CapacitaAnnualeStabilimento: '',
                        my_StabilimentoUbicato: '',
                        my_ProdottiConferitiDaiSoci: '',
                        my_ProdottiAcquistatiDaTerzi: '',
                        my_CkPrevistoProgrammaAmpliamento: '',
                        my_CkFruisceProvvidenzeAgricole: '',
                        my_SpecificaProvvidenzeAgricole: '',
                        my_CkInUsoListinoAnnuale: '',
                        my_CorrispettivoMedioListinoAnnuale: '',
                        my_DifferenzaListinoPrecedente: '',
                        my_IncidenzaPercentualeProdottoEstero: '',
                        my_AltroCoopAgricole: '',
                        my_LavorazioniSvolte: '',
                        my_CkStabilimentoPropr: '',
                        my_CkStabilimentoAffit: '',
                        my_CkStabilimentoAltroTit: '',
                        my_CommScambioMutPrevisto: '',
                        my_CommStruttOrgSediAg: '',
                        my_CommRappAgenti: '',
                        my_CommVenNolProdAgr: '',
                        my_CommGestCollett: '',
                        my_ckTerreniAQualsiasiTitolo: '',
                        my_ckSerSocMotoArat: '',
                        my_ckSerSocTrebb: '',
                        my_ckSerSocAcqVenProdAgr: '',
                        my_ckSerSocImmagOrtoFrut: '',
                        my_ckSerSocAgrum: '',
                        my_ckSerSocAltro: '',
                        my_CkDispStabilimento: '',
                        my_CkCicloBio: '',
                        my_NoteCicloBio: '',
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: SchedaCoopAgricoleFormValidators,
            }
        );
    }

    protected override afterFormInitialize() {
        this._manageDeactivation();
        manageScObbl(this.datiIdentificativiFormService, this._form, `${formPaths.schedaCoopAgricole}.my_ScAgricoleObbl`);
        manageScObbl(this.datiIdentificativiFormService, this._form, `${formPaths.datiIdentificativi}.my_TipoSezEnte`);
        manageScObbl(this.datiIdentificativiFormService, this._form, `${formPaths.datiIdentificativi}.my_ckSezAlboCorretta`);
    }

    protected override beforePopulateForm(myVerbale: MyVerbale) {
        setInitialTipoSezEnte(myVerbale, this.store);
    }

    private _manageDeactivation(): void {
        this.isDisabled$ = manageSchedaDisabled(this.store, revisione53DatiFormSelectors.getScAgricoleObbl);
    }
}
